import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaShieldAlt,
} from "react-icons/fa";
import apiRequest from "../services/api";

const RoleManagement = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedRole, setSelectedRole] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: [],
  });

  const fetchInitData = async () => {
    setIsLoading(true);
    setError("");

    try {
      const [rolesResp, permsResp] = await Promise.all([
        apiRequest("settings/roles"),
        apiRequest("settings/permissions"),
      ]);

      setRoles(rolesResp.data || []);
      setPermissions(permsResp.data || []);
    } catch (err) {
      setError(
        err?.message || "Failed to sync system security configurations."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitData();
  }, []);

  const openModal = (mode, role = null) => {
    setModalMode(mode);
    setSelectedRole(role);
    setError("");

    if (role) {
      const activePermissions = (role.permissions || []).map((p) =>
        typeof p === "string" ? p : p.name
      );

      setFormData({
        name: role.name,
        description: role.description || "",
        permissions: activePermissions,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        permissions: [],
      });
    }

    setIsModalOpen(true);
  };

  const handlePermissionToggle = (permName) => {
    if (modalMode === "view") return;

    setFormData((prev) => {
      const current = [...prev.permissions];
      const index = current.indexOf(permName);

      if (index > -1) {
        current.splice(index, 1);
      } else {
        current.push(permName);
      }

      return { ...prev, permissions: current };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    const endpoint =
      modalMode === "create" ? "settings/roles" : "settings/roles";
    const method = modalMode === "create" ? "POST" : "PUT";

    const payload = {
      ...formData,
      id: selectedRole?.id,
      permissions: formData.permissions.map((permName) => ({
        name: permName,
      })),
    };

    try {
      await apiRequest(endpoint, {
        method,
        body: payload,
      });

      fetchInitData();
      setIsModalOpen(false);
      handleLogout();
    } catch (err) {
      setError(
        err?.message || "Transaction failure while saving configurations."
      );
    }
  };

  const handleDelete = async (id, name) => {
    if (
      !window.confirm(
        `Are you sure you want to drop the security role "${name}"?`
      )
    )
      return;

    try {
      await apiRequest(`settings/roles/${id}`, {
        method: "DELETE",
      });

      fetchInitData();
    } catch (err) {
      setError(err?.message || "Failed to drop specified element block.");
    }
  };

  const getGroupedPermissions = () => {
    const groups = {};

    permissions.forEach((perm) => {
      const name = typeof perm === "string" ? perm : perm.name;
      const groupKey = name.includes("_") ? name.split("_")[0] : "SYSTEM";

      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(name);
    });

    return groups;
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  const groupedPermissions = getGroupedPermissions();

  const inputClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed transition dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50";

  const labelClass =
    "text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400";

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 relative pb-24">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <FaShieldAlt className="text-emerald-500" />
            RBAC Security Configuration
          </h1>

          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Configure system roles and authorization permissions.
          </p>
        </div>

        <button
          onClick={() => openModal("create")}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-emerald-600/20"
        >
          <FaPlus />
          Add New Role
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Roles Table */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] font-bold tracking-wider uppercase dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400">
                <th className="px-6 py-4">System Identity Code</th>
                <th className="px-6 py-4">Privileges</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-slate-700 text-xs dark:divide-white/5 dark:text-slate-300">
              {isLoading ? (
                <tr>
                  <td
                    colSpan="3"
                    className="text-center py-8 text-slate-400 dark:text-slate-500"
                  >
                    Querying identity subsystems...
                  </td>
                </tr>
              ) : roles.length === 0 ? (
                <tr>
                  <td
                    colSpan="3"
                    className="text-center py-8 text-slate-400 dark:text-slate-500"
                  >
                    No security records found
                  </td>
                </tr>
              ) : (
                roles.map((role) => (
                  <tr
                    key={role.id}
                    className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
                  >
                    {/* Role */}
                    <td className="px-6 py-4 font-semibold align-top">
                      <div className="pt-1">
                        <span className="px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-emerald-700 tracking-wide dark:bg-slate-950/60 dark:border-white/10 dark:text-emerald-400">
                          {role.name}
                        </span>
                      </div>
                    </td>

                    {/* Permissions */}
                    <td className="px-6 py-4 max-w-md align-top">
                      <div className="flex flex-wrap gap-1.5">
                        {role.permissions && role.permissions.length > 0 ? (
                          role.permissions.map((p, idx) => {
                            const pName = typeof p === "string" ? p : p.name;

                            return (
                              <span
                                key={idx}
                                className="bg-slate-100 border border-slate-200 text-slate-600 text-[10px] px-2 py-0.5 rounded-md dark:bg-slate-950/60 dark:border-white/10 dark:text-slate-400"
                              >
                                {pName}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-slate-400 italic dark:text-slate-600">
                            No privileges linked
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 text-right align-top">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openModal("view", role)}
                          className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 border border-slate-200 transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
                          title="Inspect Details"
                        >
                          <FaEye />
                        </button>

                        <button
                          onClick={() => openModal("edit", role)}
                          className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-600 border border-blue-200 transition dark:bg-blue-600/10 dark:hover:bg-blue-600/20 dark:text-blue-400 dark:border-blue-500/10"
                          title="Modify Schema"
                        >
                          <FaEdit />
                        </button>

                        <button
                          onClick={() => handleDelete(role.id, role.name)}
                          className="p-2 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 border border-red-200 transition dark:bg-red-600/10 dark:hover:bg-red-600/20 dark:text-red-400 dark:border-red-500/10"
                          title="Purge Record"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Modal Sheet */}
      <div
        className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
          isModalOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/70"
          onClick={() => setIsModalOpen(false)}
        />

        {/* Drawer */}
        <div
          className={`relative w-full max-w-4xl mx-auto bg-white border border-slate-200 rounded-t-3xl p-6 sm:p-8 shadow-[0_-15px_40px_rgba(15,23,42,0.15)] dark:bg-slate-900 dark:border-white/10 dark:shadow-[0_-15px_40px_rgba(0,0,0,0.5)] transition-transform duration-300 transform space-y-6 overflow-y-auto max-h-[85vh] ${
            isModalOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          {/* Drag Handle */}
          <div className="w-12 h-1 bg-slate-200 rounded-full mx-auto -mt-2 mb-2 dark:bg-white/20" />

          {/* Modal Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white capitalize">
                {modalMode} Role Assignment Profile
              </h3>

              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {modalMode === "view"
                  ? "Inspecting active rules bounds."
                  : "Link or strip dynamic authorization permissions mappings instantly below."}
              </p>
            </div>

            <button
              onClick={() => setIsModalOpen(false)}
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 transition-colors dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSave} className="space-y-6">
            {/* Role Name */}
            <div className="grid grid-cols-1 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>
                  Role Unique Identification Label
                </label>

                <input
                  type="text"
                  required
                  disabled={modalMode === "view"}
                  placeholder="e.g. LOAN_OFFICER"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      name: e.target.value.toUpperCase().replace(/\s+/g, "_"),
                    })
                  }
                  className={inputClass}
                />
              </div>
            </div>

            {/* Permissions Matrix */}
            <div className="space-y-4 pt-2">
              <label className={`${labelClass} block`}>
                Privileges Capability Bounds Matrix
              </label>

              {Object.keys(groupedPermissions).length === 0 ? (
                <div className="p-4 bg-slate-50 text-center rounded-xl text-slate-500 border border-slate-200 text-xs dark:bg-slate-950/30 dark:text-slate-500 dark:border-white/5">
                  No active master authorizations registered on system
                  initialization hook.
                </div>
              ) : (
                <div className="space-y-4 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                  {Object.entries(groupedPermissions).map(
                    ([group, permNames]) => (
                      <div
                        key={group}
                        className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 dark:bg-slate-950/30 dark:border-white/5"
                      >
                        <h4 className="text-xs font-bold text-slate-500 tracking-wider border-b border-slate-200 pb-1 uppercase dark:text-slate-400 dark:border-white/5">
                          {group} Management Module
                        </h4>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {permNames.map((permName) => {
                            const isChecked =
                              formData.permissions.includes(permName);

                            return (
                              <label
                                key={permName}
                                className={`flex items-start gap-3 p-2.5 rounded-lg border text-xs cursor-pointer select-none transition-all ${
                                  isChecked
                                    ? "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-600/10 dark:border-emerald-500/30 dark:text-emerald-400"
                                    : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100 dark:bg-slate-900/40 dark:border-white/5 dark:text-slate-400 dark:hover:bg-slate-800/40"
                                } ${
                                  modalMode === "view"
                                    ? "pointer-events-none opacity-80"
                                    : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={modalMode === "view"}
                                  onChange={() =>
                                    handlePermissionToggle(permName)
                                  }
                                  className="mt-0.5 accent-emerald-500 rounded border-slate-300 dark:border-slate-700 transition-all"
                                />

                                <span className="break-all tracking-wide">
                                  {permName}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 text-xs font-semibold transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
              >
                Close
              </button>

              {modalMode !== "view" && (
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-md shadow-emerald-600/10 transition"
                >
                  Save
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;
